import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react'
import cytoscape from 'cytoscape'
import type { Core, ElementDefinition } from 'cytoscape'
import type { MockEdge, MockNode, MockNodeType } from './types'
import { computeRootIds } from './computeRootIds'
import { buildLayoutOptions, type GraphLayout } from './layoutOptions'
import { NODE_TYPE_STYLES, NODE_TYPES } from './nodeIcons'

export interface OverviewNode {
  id: string
  x: number
  y: number
  type: MockNodeType
}

export interface OverviewEdge {
  source: string
  target: string
}

export interface Overview {
  nodes: OverviewNode[]
  edges: OverviewEdge[]
  /** Bounding box of all nodes in graph coordinates. */
  bbox: { x1: number; y1: number; x2: number; y2: number }
}

export interface Viewport {
  /** Visible extent in graph coordinates. */
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface CanvasHandle {
  /** Set the absolute cytoscape zoom level, centered on the viewport. Clamped
   *  to the canvas's zoom bounds. Drives +/- and type-in via `useZoom`. */
  setZoom: (level: number) => void
  fit: () => void
  getZoom: () => number
  subscribe: (fn: (zoom: number) => void) => () => void
  /** Center the view on a node and select it. No-op if the node id isn't found. */
  focusNode: (id: string) => void
  /** Returns a snapshot of all node/edge positions for minimap rendering. */
  getOverview: () => Overview | null
  /** Subscribes to viewport (pan/zoom) changes for the minimap's viewport indicator. */
  subscribeViewport: (fn: (vp: Viewport) => void) => () => void
  /** Cytoscape zoom level that fits the full graph in the viewport. Used by
   *  ZoomControls to display zoom as a percentage where "100%" = whole
   *  graph visible. */
  getFitZoom: () => number
  /** Cytoscape's hard zoom bounds. ZoomControls calibrates the +/- enabled
   *  state to these so the buttons disable at the real floor/ceiling (not just
   *  the percent caps) on graphs whose fit baseline is small (FS-562). */
  getMinZoom: () => number
  getMaxZoom: () => number
  /** Position updates for the minimap, on canvases whose nodes move (the D3
   *  physics, a layout switch). Absent on the static cytoscape canvas. */
  subscribeOverview?: (fn: (overview: Overview) => void) => () => void
}

export interface HoverInfo {
  id: string
  /** Rendered position of the right edge of the node, in canvas-container pixels. */
  x: number
  y: number
}

interface Props {
  nodes: MockNode[]
  edges: MockEdge[]
  enabledTypes: Set<MockNodeType>
  showEdgeLabels?: boolean
  /** Show each node's name under its icon. Off keeps names in the hover card
   *  and details panel only, which is what the tag drill-down expects. */
  showEntityLabels?: boolean
  /** Show nodes that have no connection in the currently filtered graph.
   *  Off hides them (display none) without touching the data; on parks them
   *  in their own column beside the graph. The enterprise root is never
   *  treated as an orphan. On by default for callers that do not offer the
   *  toggle. */
  showOrphans?: boolean
  /** Width of every relationship line, in px. */
  lineWidth?: number
  /** Node ids the page's file-type / status filters exclude; dimmed like a
   *  type that is switched off, so the three facets compose. */
  filteredOutIds?: Set<string>
  /** Fired when a node is tapped; pass null when the background is tapped. */
  onNodeSelect?: (nodeId: string | null) => void
  /** Currently-selected node id (so external selection reflects on the canvas). */
  selectedNodeId?: string | null
  /** Fired on node mouseover (with anchor coords) and mouseout (null). */
  onNodeHover?: (info: HoverInfo | null) => void
  /** Free-text filter — nodes whose label doesn't contain this string are
   *  dimmed (along with their edges), case-insensitive. Empty string or
   *  undefined disables search filtering. */
  searchQuery?: string
  /** Radial (concentric rings by depth) or tree (top-down breadthfirst).
   *  Changing it re-runs the layout in place with a short glide; the graph is
   *  not rebuilt. Defaults to tree, which is what the tag drill-down expects. */
  layout?: GraphLayout
}

const DEFAULT_EDGE_LABEL = 'related to'
const DEFAULT_LINE_WIDTH = 1.5
// The orphan area: a column to the right of the connected graph, one node
// per row, so unconnected nodes stay discoverable without sitting on a ring
// or a rank they do not belong to (the designer's Orphans note, "on").
const ORPHAN_AREA_GAP = 160
const ORPHAN_AREA_ROW = 72

const parkOrphans = (cy: Core) => {
  const orphans = cy.nodes('.orphan')
  if (orphans.empty()) return
  const rest = cy.nodes().not('.orphan')
  const bb = rest.nonempty() ? rest.boundingBox({}) : { x1: 0, y1: 0, x2: 0, y2: 0 }
  const x = bb.x2 + ORPHAN_AREA_GAP
  let y = bb.y1
  orphans.forEach((node) => {
    node.position({ x, y })
    y += ORPHAN_AREA_ROW
  })
}
const EDGE_LABELS: Record<string, string> = {
  'enterprise>site': 'has site',
  'site>operating_area': 'contains',
  'operating_area>operation_unit': 'contains',
  'operation_unit>system': 'has system',
  'system>functional_location': 'at location',
  'functional_location>components': 'installed at',
  'operation_unit>documents': 'documented by',
  'components>documents': 'documented by',
}

const KnowledgeGraphCanvas = forwardRef<CanvasHandle, Props>(({ nodes, edges, enabledTypes, showEdgeLabels = true, onNodeSelect, selectedNodeId, onNodeHover, searchQuery, layout = 'tree', showEntityLabels = false, showOrphans = true, lineWidth = DEFAULT_LINE_WIDTH, filteredOutIds }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const cyRef = useRef<Core | null>(null)
  const subscribersRef = useRef<Set<(z: number) => void>>(new Set())
  const viewportSubsRef = useRef<Set<(vp: Viewport) => void>>(new Set())
  // Cytoscape zoom level at which the full graph fits in the viewport,
  // captured right after layout. Used to calibrate the percentage display
  // so "100%" = fit-to-screen.
  const fitZoomRef = useRef(1)
  // Read by the build effect so a layout change does not rebuild cytoscape;
  // the layout effect below re-runs the layout on the live instance instead.
  const layoutRef = useRef<GraphLayout>(layout)
  layoutRef.current = layout

  // Fit-to-viewport leaves the graph too small to read when the hierarchy
  // is only a few wide layers, so the actual default view is fit × 1.73.
  // We then calibrate "100%" to that default zoom — the ZoomControls label
  // reads `(zoom / fitZoom) * 100`, so anchoring fitZoomRef at the post-1.73×
  // level means the user sees 100% on first paint even though we're well
  // past raw fit-to-screen. Shared by the first paint and every re-layout.
  const applyDefaultView = (cy: Core) => {
    cy.fit(undefined, 60)
    const target = Math.min(cy.zoom() * 1.73, 2)
    cy.zoom({ level: target, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    fitZoomRef.current = target
  }

  useImperativeHandle(ref, () => ({
    // Absolute zoom; the +/- buttons and type-in compute the target level via
    // `useZoom` (fit-baseline model) and push it here. Centered on the viewport
    // and clamped to [minZoom, maxZoom] by cytoscape.
    setZoom: (level: number) => {
      const cy = cyRef.current
      if (!cy) return
      cy.zoom({ level, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    },
    fit: () => {
      const cy = cyRef.current
      if (!cy) return
      // Reset to the "default view" = fit × 1.73 (same calibration as the
      // initial paint), so the percentage label round-trips back to 100%.
      applyDefaultView(cy)
    },
    getZoom: () => cyRef.current?.zoom() ?? 1,
    getFitZoom: () => fitZoomRef.current,
    getMinZoom: () => cyRef.current?.minZoom() ?? 0.3,
    getMaxZoom: () => cyRef.current?.maxZoom() ?? 2,
    subscribe: (fn) => {
      subscribersRef.current.add(fn)
      return () => subscribersRef.current.delete(fn) as unknown as void
    },
    focusNode: (id) => {
      const cy = cyRef.current
      if (!cy) return
      const node = cy.getElementById(id)
      if (!node || node.empty()) return
      cy.elements().unselect()
      node.select()
      cy.animate({ center: { eles: node }, zoom: 1 }, { duration: 350 })
    },
    getOverview: () => {
      const cy = cyRef.current
      if (!cy) return null
      const overviewNodes: OverviewNode[] = cy.nodes().map((n) => {
        const pos = n.position()
        return {
          id: n.id(),
          x: pos.x,
          y: pos.y,
          type: n.data('type') as MockNodeType,
        }
      })
      const overviewEdges: OverviewEdge[] = cy.edges().map((e) => ({
        source: e.source().id(),
        target: e.target().id(),
      }))
      const bb = cy.elements().boundingBox({})
      return {
        nodes: overviewNodes,
        edges: overviewEdges,
        bbox: { x1: bb.x1, y1: bb.y1, x2: bb.x2, y2: bb.y2 },
      }
    },
    subscribeViewport: (fn) => {
      viewportSubsRef.current.add(fn)
      const cy = cyRef.current
      if (cy) {
        const ext = cy.extent()
        fn({ x1: ext.x1, y1: ext.y1, x2: ext.x2, y2: ext.y2 })
      }
      return () => void viewportSubsRef.current.delete(fn)
    },
  }))

  useEffect(() => {
    if (!containerRef.current) return

    const nodeTypeById = new Map(nodes.map((node) => [node.id, node.type]))
    // Layout roots. Prefer nodes with no INCOMING edge — the actual tops of a
    // directed graph. The previous rule (`!n.parent`) treats every node without
    // a compound parent as a root, and for a graph that uses plain edges rather
    // than compound nesting that is ALL of them, which collapses breadthfirst
    // into a single rank (one flat row instead of a tree). Falls back to the old
    // rule when everything has an incoming edge, e.g. a cycle.
    const rootIds = computeRootIds(nodes, edges)
    const elements: ElementDefinition[] = [
      ...nodes.map((node) => ({
        data: { id: node.id, label: node.label, type: node.type },
        classes: node.type,
      })),
      ...edges.map((e) => {
        const key = `${nodeTypeById.get(e.source)}>${nodeTypeById.get(e.target)}`
        return {
          data: {
            id: e.id,
            source: e.source,
            target: e.target,
            label: e.label ?? EDGE_LABELS[key] ?? DEFAULT_EDGE_LABEL,
          },
        }
      }),
    ]

    cyRef.current = cytoscape({
      container: containerRef.current,
      elements,
      layout: buildLayoutOptions({ layout: layoutRef.current, nodes, edges, rootIds, animate: false }),
      // 0.2 made wheel zoom feel glacial (5x slower than cytoscape default) and
      // maxZoom 2 couldn't get close enough to read labels/flow arrows on big
      // graphs. 0.8 ~= default feel slightly softened for trackpads; 0.1..8
      // covers whole-graph overview through close inspection.
      wheelSensitivity: 0.8,
      minZoom: 0.1,
      maxZoom: 8,
      style: [
        // Base: circular icon-only nodes per Figma 13043-183460. Per-type
        // styles below set the actual fill, border, size and the inline
        // Lucide SVG used as the background image. The text label is hidden
        // on the node itself — names live in the hover card / details panel.
        {
          selector: 'node',
          style: {
            shape: 'ellipse',
            'background-color': '#f5f5f5',
            'background-image-opacity': 1,
            'background-fit': 'none',
            'background-clip': 'node',
            'border-width': 1,
            'border-color': '#737373',
            label: '',
            width: 24,
            height: 24,
            // Only opacity transitions — animating border-color / border-width
            // makes cytoscape redraw the canvas every frame for ~200ms, which
            // re-rasterizes every node's SVG data-URI icon and shows up as a
            // visible blink across all icons whenever any one node is hovered.
            'transition-property': 'opacity',
            'transition-duration': 200,
          },
        },
        ...NODE_TYPES.map((type) => {
          const style = NODE_TYPE_STYLES[type]
          return {
            selector: `node.${type}`,
            style: {
              'background-color': style.bg,
              'border-color': style.fg,
              'background-image': style.iconDataUri,
              'background-width': `${style.iconSize}px`,
              'background-height': `${style.iconSize}px`,
              width: style.size,
              height: style.size,
            },
          }
        }),
        { selector: 'node.dimmed', style: { opacity: 0.15 } },
        // Entity Label on: the name under the icon (Figma "Entity Label = On").
        {
          selector: 'node.show-label',
          style: {
            label: 'data(label)',
            'font-size': 11,
            'font-family': 'Inter, system-ui, sans-serif',
            color: '#525252',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'text-margin-y': 6,
            'text-max-width': '140px',
            'text-wrap': 'ellipsis',
          },
        },
        // Orphans off: hidden, not removed, so the data and counts stand.
        { selector: 'node.hidden-orphan', style: { display: 'none' } },
        { selector: 'node:selected', style: { 'border-width': 3 } },
        // Hover: cyan ring around the node, matches Figma "States" frames.
        { selector: 'node.hover', style: { 'border-width': 2, 'border-color': '#06b6d4' } },
        {
          selector: 'edge',
          style: {
            width: DEFAULT_LINE_WIDTH,
            'line-color': '#d4d4d8',
            'curve-style': 'unbundled-bezier',
            'control-point-distances': [-20],
            'control-point-weights': [0.5],
            'target-arrow-shape': 'none',
            'transition-property': 'opacity, line-color',
            'transition-duration': 200,
          },
        },
        { selector: 'edge.dimmed', style: { opacity: 0.1 } },
        {
          selector: 'edge.show-label',
          style: {
            label: 'data(label)',
            'font-size': 10,
            'font-family': 'Inter, system-ui, sans-serif',
            color: '#737373',
            'text-rotation': 'autorotate',
            'text-background-color': '#ffffff',
            'text-background-opacity': 1,
            'text-background-padding': '2px',
          },
        },
      ],
    })

    applyDefaultView(cyRef.current)

    const cy = cyRef.current
    const onZoom = () => subscribersRef.current.forEach((fn) => fn(cy.zoom()))
    const onViewport = () => {
      const ext = cy.extent()
      const vp: Viewport = { x1: ext.x1, y1: ext.y1, x2: ext.x2, y2: ext.y2 }
      viewportSubsRef.current.forEach((fn) => fn(vp))
    }
    cy.on('zoom', onZoom)
    cy.on('pan zoom resize', onViewport)
    onZoom()
    onViewport()

    const handleTap = (e: cytoscape.EventObject) => {
      if (e.target === cy) {
        onNodeSelect?.(null)
      } else if (e.target.isNode?.()) {
        onNodeSelect?.(e.target.id())
      }
    }
    cy.on('tap', handleTap)
    // A rebuild (new nodes/edges) starts from the stylesheet defaults; the
    // other effects only run when their own prop changes, so re-apply here.
    cy.edges().toggleClass('show-label', showEdgeLabels)
    cy.nodes().toggleClass('show-label', showEntityLabels)
    cy.edges().style('width', lineWidth)

    const handleMouseOver = (e: cytoscape.EventObject) => {
      const node = e.target
      if (!node?.isNode?.()) return
      node.addClass('hover')
      if (!onNodeHover) return
      const bb = node.renderedBoundingBox()
      onNodeHover({ id: node.id(), x: bb.x2, y: (bb.y1 + bb.y2) / 2 })
    }
    const handleMouseOut = (e: cytoscape.EventObject) => {
      const node = e.target
      if (!node?.isNode?.()) return
      node.removeClass('hover')
      onNodeHover?.(null)
    }
    cy.on('mouseover', 'node', handleMouseOver)
    cy.on('mouseout', 'node', handleMouseOut)
    // Keep the tooltip pinned to the node while the user pans/zooms.
    const refreshHoverAnchor = () => {
      if (!onNodeHover) return
      const hovered = cy.nodes('.hover')
      if (hovered.empty()) return
      const n = hovered[0]
      const bb = n.renderedBoundingBox()
      onNodeHover({ id: n.id(), x: bb.x2, y: (bb.y1 + bb.y2) / 2 })
    }
    cy.on('pan zoom', refreshHoverAnchor)

    return () => {
      cy.off('tap', handleTap)
      cy.off('mouseover', 'node', handleMouseOver)
      cy.off('mouseout', 'node', handleMouseOut)
      cy.off('pan zoom', refreshHoverAnchor)
      cy.off('zoom', onZoom)
      cy.off('pan zoom resize', onViewport)
      cy.destroy()
      cyRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the display toggles have their own effects; listing them here would rebuild the graph on every switch
  }, [nodes, edges, onNodeSelect, onNodeHover])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    const query = (searchQuery ?? '').trim().toLowerCase()
    cy.batch(() => {
      cy.nodes().forEach((node) => {
        const type = node.data('type') as MockNodeType
        const label = String(node.data('label') ?? '').toLowerCase()
        const typeVisible = enabledTypes.size === 0 || enabledTypes.has(type) || type === 'enterprise'
        // Empty query → no search filter. Enterprise root is exempt so the
        // graph always has an anchor visible even when the query matches nothing.
        const searchVisible = !query || type === 'enterprise' || label.includes(query)
        const facetVisible = !filteredOutIds?.has(node.id())
        node.toggleClass('dimmed', !(typeVisible && searchVisible && facetVisible))
      })
      cy.edges().forEach((edge) => {
        edge.toggleClass('dimmed', edge.source().hasClass('dimmed') || edge.target().hasClass('dimmed'))
      })
      // An orphan has no connection left in the filtered graph: every edge it
      // had is dimmed away, or it never had one. The enterprise root anchors
      // the graph and is exempt, as the designer's note says a root with
      // connected children is not an orphan.
      cy.nodes().forEach((node) => {
        const type = node.data('type') as MockNodeType
        const connected = node.connectedEdges().not('.dimmed').nonempty()
        const orphan = type !== 'enterprise' && !connected
        node.toggleClass('orphan', orphan)
        node.toggleClass('hidden-orphan', orphan && !showOrphans)
      })
    })
    if (showOrphans) parkOrphans(cy)
  }, [enabledTypes, searchQuery, showOrphans, filteredOutIds])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    cy.edges().toggleClass('show-label', showEdgeLabels)
  }, [showEdgeLabels])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    cy.nodes().toggleClass('show-label', showEntityLabels)
  }, [showEntityLabels])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    // Applied as the slider moves; a stylesheet update would re-rasterize
    // every node icon, a per-edge bypass touches only the lines.
    cy.edges().style('width', lineWidth)
  }, [lineWidth])

  // Re-run the layout in place when the toolbar switches it. The build effect
  // already laid the graph out with the current value, so the first run is
  // skipped; afterwards nodes glide to their new rings/rows and the view
  // resets to the default fit so the percentage label reads 100% again.
  const appliedLayoutRef = useRef<GraphLayout>(layout)
  useEffect(() => {
    const cy = cyRef.current
    if (!cy || appliedLayoutRef.current === layout) return
    appliedLayoutRef.current = layout
    const rootIds = computeRootIds(nodes, edges)
    const run = cy.layout(buildLayoutOptions({ layout, nodes, edges, rootIds, animate: true }))
    run.one('layoutstop', () => {
      // The layout moved the orphans with everyone else; put them back in their area.
      if (cy.nodes('.orphan').nonempty() && cy.nodes('.hidden-orphan').empty()) parkOrphans(cy)
      applyDefaultView(cy)
    })
    run.run()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nodes/edges changes rebuild cytoscape above
  }, [layout])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    cy.elements().unselect()
    if (selectedNodeId) {
      const n = cy.getElementById(selectedNodeId)
      if (!n.empty()) n.select()
    }
  }, [selectedNodeId])

  return <div ref={containerRef} className="w-full h-full" />
})
KnowledgeGraphCanvas.displayName = 'KnowledgeGraphCanvas'

export default KnowledgeGraphCanvas
